/* eslint-disable react/jsx-key */
// @ts-ignore
import React from "react";

import { css } from "@compiled/react";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { Document } from "@contentful/rich-text-types";

import { token } from "@atlaskit/tokens";

import { AgileVideo } from "@/components/agile-video/agile-video";
import { Image } from "@/libraries/design/components";
import { Panel } from "@/libraries/design/components/panel/src/panel";
import { RichTextComponent } from "@/templates/agile-hub/src/sections/components/body-text";
import {
    BodyTextContainer,
    isAssetComponent,
    isEmbedComponent,
    isProTipText,
    isQuestionComponent,
    isCallOutComponent,
    isTextComponent
} from "@/templates/agile-hub/types";

type Nullable<T> = T | null;

export interface UniformBodyContentProps {
    bodyTextContainer: Nullable<BodyTextContainer>;
}

function getRichText(richText: string | Document) {
    if (typeof richText === "string") {
        return richText;
    }

    return documentToHtmlString(richText);
}

export const AgileArticleBodyContent = ({
    bodyTextContainer
}: UniformBodyContentProps) => {
    return (
        <div
            data-testid="agile-hub-article-content"
            css={css({
                display: "flex",
                flexDirection: "column",
                gap: token("space.250")
            })}
        >
            <h2
                css={css({
                    marginBlockEnd: token("space.0")
                })}
            >
                {bodyTextContainer?.name}
            </h2>
            {/* eslint-disable-next-line complexity */}
            {bodyTextContainer?.contentComponents?.map((component) => {
                if (isTextComponent(component)) {
                    return (
                        <div
                            css={css({
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                "div > :last-child": {
                                    marginBlockEnd: 0
                                },
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                "div > :first-child": {
                                    marginBlockStart: 0
                                },
                                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                                "div > h2, h3": {
                                    marginBlockEnd: token("space.250")
                                }
                            })}
                        >
                            <RichTextComponent
                                name={component?.name ?? ""}
                                text={getRichText(component?.text ?? "")}
                            />
                        </div>
                    );
                } else if (isAssetComponent(component)) {
                    return (
                        <div
                            css={css({
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center"
                            })}
                        >
                            <Image
                                css={css({
                                    maxWidth: "100%"
                                })}
                                src={component?.asset?.url ?? ""}
                                alt={component?.asset?.description ?? ""}
                                contentType={
                                    component?.asset?.contentType ?? ""
                                }
                            />
                            {component?.caption && (
                                <p
                                    className="fine-print"
                                    css={css({
                                        textAlign: "center",
                                        margin: 0
                                    })}
                                >
                                    {component?.caption}
                                </p>
                            )}
                        </div>
                    );
                } else if (isEmbedComponent(component)) {
                    return (
                        <AgileVideo
                            embedLink={component?.embedded || ""}
                            thumbnail={component?.embedAssetOverlay}
                            title={component?.embeddedVideoAssetName ?? ""}
                        />
                    );
                } else if (isProTipText(component)) {
                    return (
                        <Panel
                            panelType="pro-tip"
                            label={component?.name ?? ""}
                            description={
                                <RichTextComponent
                                    name={component?.name ?? ""}
                                    text={getRichText(
                                        component?.proTipText ?? ""
                                    )}
                                />
                            }
                        />
                    );
                } else if (isCallOutComponent(component)) {
                    return (
                        <Panel
                            panelType="highlight"
                            label={component?.title ?? ""}
                            description={
                                <RichTextComponent
                                    name={component?.title ?? ""}
                                    text={getRichText(
                                        component?.callOutText ?? ""
                                    )}
                                />
                            }
                        />
                    );
                } else if (isQuestionComponent(component)) {
                    return (
                        <Panel
                            panelType="highlight"
                            label={component?.questionTitle ?? ""}
                            description={
                                <RichTextComponent
                                    name={component?.questionTitle ?? ""}
                                    text={getRichText(component?.answer ?? "")}
                                />
                            }
                        />
                    );
                }
                return null;
            })}
        </div>
    );
};
